import React, { useState, useEffect } from 'react';
import { 
  BuildingOfficeIcon, 
  PhoneIcon, 
  EnvelopeIcon, 
  MapPinIcon,
  GlobeAltIcon,
  ArrowPathIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import { toast } from 'react-toastify';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

const SiteSettingsPage = () => {
  const [settings, setSettings] = useState({
    schoolName: '',
    schoolMotto: '',
    address: '',
    phone: '',
    email: '',
    facebookUrl: '',
    twitterUrl: '',
    instagramUrl: '',
    youtubeUrl: '',
    description: ''
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [originalSettings, setOriginalSettings] = useState({});

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/site-settings`);
      
      if (response.ok) {
        const data = await response.json();
        setSettings({
          schoolName: data.schoolName || '',
          schoolMotto: data.schoolMotto || '',
          address: data.address || '',
          phone: data.phone || '',
          email: data.email || '',
          facebookUrl: data.facebookUrl || '',
          twitterUrl: data.twitterUrl || '',
          instagramUrl: data.instagramUrl || '',
          youtubeUrl: data.youtubeUrl || '',
          description: data.description || ''
        });
        setOriginalSettings({
          schoolName: data.schoolName || '',
          schoolMotto: data.schoolMotto || '',
          address: data.address || '',
          phone: data.phone || '',
          email: data.email || '',
          facebookUrl: data.facebookUrl || '',
          twitterUrl: data.twitterUrl || '',
          instagramUrl: data.instagramUrl || '',
          youtubeUrl: data.youtubeUrl || '',
          description: data.description || ''
        });
        setHasChanges(false);
      } else {
        toast.error('Failed to fetch site settings');
      }
    } catch (error) {
      console.error('Error fetching site settings:', error);
      toast.error('Error loading site settings');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setSettings(prev => {
      const newSettings = { ...prev, [name]: value };
      setHasChanges(JSON.stringify(newSettings) !== JSON.stringify(originalSettings));
      return newSettings;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Basic validation
    if (!settings.schoolName.trim()) {
      toast.error('School name is required');
      return;
    }
    
    if (!settings.address.trim()) {
      toast.error('Address is required');
      return;
    }
    
    if (!settings.phone.trim()) {
      toast.error('Phone number is required');
      return;
    }
    
    if (!settings.email.trim()) {
      toast.error('Email is required');
      return;
    }
    
    try {
      setSaving(true);
      const response = await fetch(`${API_BASE_URL}/site-settings`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        },
        body: JSON.stringify(settings)
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success('Site settings updated successfully!');
        setOriginalSettings({ ...settings });
        setHasChanges(false);
        // Refresh the settings from server
        fetchSettings();
      } else {
        const errorData = await response.json();
        toast.error(errorData.message || 'Failed to update site settings');
      }
    } catch (error) {
      console.error('Error updating site settings:', error);
      toast.error('Error updating site settings');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm('Are you sure you want to reset all settings to default values? This action cannot be undone.')) {
      return;
    }
    
    try {
      setResetting(true);
      const response = await fetch(`${API_BASE_URL}/site-settings/reset`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }
      });
      
      if (response.ok) {
        toast.success('Site settings reset to defaults successfully!');
        fetchSettings();
      } else {
        const errorData = await response.json();
        toast.error(errorData.message || 'Failed to reset site settings');
      }
    } catch (error) {
      console.error('Error resetting site settings:', error);
      toast.error('Error resetting site settings');
    } finally {
      setResetting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Site Settings</h1>
              <p className="mt-1 text-sm text-gray-500">
                Manage your site's contact information and social media links
              </p>
            </div>
            <div className="flex space-x-3">
              <button
                onClick={handleReset}
                disabled={resetting}
                className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50"
              >
                <ArrowPathIcon className="-ml-1 mr-2 h-4 w-4" />
                {resetting ? 'Resetting...' : 'Reset to Defaults'}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Settings Form */}
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* School Information */}
        <div className="bg-white shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <div className="flex items-center mb-4">
              <BuildingOfficeIcon className="h-5 w-5 text-primary mr-2" />
              <h2 className="text-lg font-medium text-gray-900">School Information</h2>
            </div>
            
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
              <div>
                <label htmlFor="schoolName" className="block text-sm font-medium text-gray-700">
                  School Name *
                </label>
                <input
                  type="text"
                  name="schoolName"
                  id="schoolName"
                  required
                  value={settings.schoolName}
                  onChange={handleInputChange}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary sm:text-sm"
                  placeholder="East End Prep"
                />
              </div>
              
              <div>
                <label htmlFor="schoolMotto" className="block text-sm font-medium text-gray-700">
                  School Motto
                </label>
                <input
                  type="text"
                  name="schoolMotto"
                  id="schoolMotto"
                  value={settings.schoolMotto}
                  onChange={handleInputChange}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary sm:text-sm"
                  placeholder="Eagles Believe, Eagles Achieve"
                />
              </div>
            </div>
            
            <div className="mt-6">
              <label htmlFor="description" className="block text-sm font-medium text-gray-700">
                Description
              </label>
              <textarea
                name="description"
                id="description"
                rows={3}
                value={settings.description}
                onChange={handleInputChange}
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary sm:text-sm"
                placeholder="Empowering student-athletes to excel both on the field and in the classroom..."
              />
            </div>
          </div>
        </div>

        {/* Contact Information */}
        <div className="bg-white shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <div className="flex items-center mb-4">
              <PhoneIcon className="h-5 w-5 text-primary mr-2" />
              <h2 className="text-lg font-medium text-gray-900">Contact Information</h2>
            </div>
            
            <div className="grid grid-cols-1 gap-6">
              <div>
                <label htmlFor="address" className="block text-sm font-medium text-gray-700">
                  <MapPinIcon className="inline h-4 w-4 mr-1" />
                  Mailing Address *
                </label>
                <textarea
                  name="address"
                  id="address"
                  rows={2}
                  required
                  value={settings.address}
                  onChange={handleInputChange}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary sm:text-sm"
                  placeholder="123 Eagle Way, East End, NY 12345"
                />
              </div>
              
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-700">
                    <PhoneIcon className="inline h-4 w-4 mr-1" />
                    Phone Number *
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    id="phone"
                    required
                    value={settings.phone}
                    onChange={handleInputChange}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary sm:text-sm"
                    placeholder="(555) 123-PREP"
                  />
                </div>
                
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                    <EnvelopeIcon className="inline h-4 w-4 mr-1" />
                    Email Address *
                  </label>
                  <input
                    type="email"
                    name="email"
                    id="email"
                    required
                    value={settings.email}
                    onChange={handleInputChange}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary sm:text-sm"
                    placeholder="athletics@eastendprep.edu"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Social Media Links */}
        <div className="bg-white shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <div className="flex items-center mb-4">
              <GlobeAltIcon className="h-5 w-5 text-primary mr-2" />
              <h2 className="text-lg font-medium text-gray-900">Social Media Links</h2>
            </div>
            
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
              <div>
                <label htmlFor="facebookUrl" className="block text-sm font-medium text-gray-700">
                  Facebook URL
                </label>
                <input
                  type="url"
                  name="facebookUrl"
                  id="facebookUrl"
                  value={settings.facebookUrl}
                  onChange={handleInputChange}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary sm:text-sm"
                  placeholder="https://facebook.com/eastendprep"
                />
              </div>
              
              <div>
                <label htmlFor="twitterUrl" className="block text-sm font-medium text-gray-700">
                  Twitter URL
                </label>
                <input
                  type="url"
                  name="twitterUrl"
                  id="twitterUrl"
                  value={settings.twitterUrl}
                  onChange={handleInputChange}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary sm:text-sm"
                  placeholder="https://twitter.com/eastendprep"
                />
              </div>
              
              <div>
                <label htmlFor="instagramUrl" className="block text-sm font-medium text-gray-700">
                  Instagram URL
                </label>
                <input
                  type="url"
                  name="instagramUrl"
                  id="instagramUrl"
                  value={settings.instagramUrl}
                  onChange={handleInputChange}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary sm:text-sm"
                  placeholder="https://instagram.com/eastendprep"
                />
              </div>
              
              <div>
                <label htmlFor="youtubeUrl" className="block text-sm font-medium text-gray-700">
                  YouTube URL
                </label>
                <input
                  type="url"
                  name="youtubeUrl"
                  id="youtubeUrl"
                  value={settings.youtubeUrl}
                  onChange={handleInputChange}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary sm:text-sm"
                  placeholder="https://youtube.com/eastendprep"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Save Button */}
        <div className="bg-white shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                {hasChanges && (
                  <div className="flex items-center text-amber-600 mr-4">
                    <ExclamationTriangleIcon className="h-4 w-4 mr-1" />
                    <span className="text-sm">You have unsaved changes</span>
                  </div>
                )}
                {!hasChanges && originalSettings.schoolName && (
                  <div className="flex items-center text-green-600 mr-4">
                    <CheckCircleIcon className="h-4 w-4 mr-1" />
                    <span className="text-sm">All changes saved</span>
                  </div>
                )}
              </div>
              
              <button
                type="submit"
                disabled={saving || !hasChanges}
                className="inline-flex items-center px-6 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {saving ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Saving...
                  </>
                ) : (
                  'Save Settings'
                )}
              </button>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};

export default SiteSettingsPage;